import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { actions } from 'features/biller/slice';
import { applyDiscount as applyDiscountThunk } from 'features/biller/thunks';
import usePromoCodePlans from 'features/managePlan/hooks/usePromoCodePlans';
import { getMarketingAttachedDiscountCoupon } from 'features/managePlan/selectors';

interface ApplyDiscountArgs {
  discountCode: string;
  planName: string;
  billingCycle?: string | null;
  productUuid?: string | null | undefined;
}

// Typed wrapper for the applyDiscount thunk, this is needed
// because it is not typed and throws an error when attempting
// to pass the arguments as expected by the thunk.
const applyDiscount = (params: ApplyDiscountArgs) =>
  (applyDiscountThunk as any)(params);

export type OnApplyDiscount = ({
  planName,
  billingCycle,
  discountCode,
  productUuid,
}: {
  planName: string;
  billingCycle: string;
  discountCode: string | null;
  productUuid?: string | null | undefined;
}) => Promise<{ payload: Record<string, unknown> }>;

export const useApplyDiscount = ({
  planName,
  billingCycle,
  discountCode,
  productUuid,
}: {
  planName: string;
  billingCycle: string;
  discountCode: string | null;
  productUuid?: string | null | undefined;
}) => {
  const dispatch = useDispatch();

  const manuallyApplyDiscount = useCallback(() => {
    if (discountCode) {
      dispatch(
        applyDiscount({
          discountCode,
          planName,
          billingCycle,
          productUuid,
        })
      );
    }
  }, [billingCycle, discountCode, dispatch, planName, productUuid]);

  return {
    manuallyApplyDiscount,
  };
};

export const useAutoApplyDiscount = ({
  subscriptionType,
  productUuid,
}: {
  subscriptionType: string;
  productUuid?: string | null | undefined;
}) => {
  const dispatch = useDispatch();

  const marketingAttachedDiscountCoupon = useSelector(
    getMarketingAttachedDiscountCoupon
  );
  const { plans: promoCodePlans } = usePromoCodePlans(
    marketingAttachedDiscountCoupon
  );

  const shouldApplyDiscount = useMemo(() => {
    if (!promoCodePlans) return false;
    return promoCodePlans.some(
      plan => plan.plan === subscriptionType.toLowerCase()
    );
  }, [promoCodePlans, subscriptionType]);

  useEffect(() => {
    if (shouldApplyDiscount) {
      // Call the thunk directly with proper parameters
      dispatch(
        applyDiscount({
          discountCode: marketingAttachedDiscountCoupon,
          planName: subscriptionType,
          billingCycle: 'monthly',
          productUuid,
        })
      );
    }

    return () => {
      dispatch(actions.removeDiscount());
    };
  }, [
    shouldApplyDiscount,
    dispatch,
    marketingAttachedDiscountCoupon,
    subscriptionType,
    productUuid,
  ]);
};
